<template>
    <div>
        <q-banner
            v-if="showFailedProductsBanner"
            class="background-red border-red q-mb-md q-mr-sm"
            rounded
            inline-actions
        >
            <template v-slot:avatar>
                <q-icon
                    name="fa-light fa-circle-exclamation"
                    color="negative"
                    class="q-pt-sm"
                    size="sm"
                />
            </template>
            <div class="text-negative q-pa-sm">
                {{ $t('Failed to update these products') }}:
                <div
                    class="q-pt-sm"
                    v-for="(productName, index) in failedProducts"
                    :key="`error-${index}`"
                >
                    - {{ productName }}
                </div>
            </div>
            <template v-slot:action>
                <q-btn
                    outline
                    @click="showFailedProductsBanner = false"
                    :label="$t('Dismiss')"
                    color="negative"
                />
            </template>
        </q-banner>
        <!-- product list -->
        <q-table
            v-if="!isSelectMode"
            @request="onRequest"
            :filter="selectedProductsTable.search"
            :rows="products"
            :columns="selectedProductsTable.cols"
            v-model:pagination="selectedProductsTable.pagination"
            :rows-per-page-options="[
                selectedProductsTable.pagination.rowsPerPage
            ]"
            :class="$q.screen.gt.lg ? 'sticky-table products-table' : ''"
            class="q-ma-none"
            row-key="id"
            binary-state-sort
            flat
            :no-data-label="$t('No data available')"
            :grid="$q.screen.lt.lg"
            :visible-columns="visibleColumns"
            wrap-cells
        >
            <!-- search bar -->
            <template v-slot:top>
                <div class="col-12">
                    <div class="text-h5 text-weight-medium">
                        {{ $t('Products for') + ' ' + asset?.name }}
                    </div>
                    <div class="page-description">
                        {{
                            $t(
                                'Manage and view products related to this asset.'
                            )
                        }}
                    </div>
                </div>
                <div
                    class="row fit q-mb-sm"
                    :class="
                        $q.screen.lt.sm
                            ? ''
                            : 'wrap justify-between items-start content-start'
                    "
                >
                    <q-input
                        data-test="search-product-input"
                        v-model="selectedProductsTable.search"
                        @clear="removeFilteredProductFromStore"
                        :placeholder="$t('Search product')"
                        :class="
                            $q.screen.lt.sm ? 'col-12 q-mb-md' : 'col q-mr-md'
                        "
                        debounce="750"
                        clearable
                        outlined
                        dense
                        @input="setFilteredProductInStore"
                        color="accent"
                    >
                        <template v-slot:prepend>
                            <q-icon name="search" />
                        </template>
                    </q-input>
                    <q-btn
                        id="add-product-button"
                        @click="isSelectMode = true"
                        :label="$t('Add product')"
                        :disable="$userCannot('edit', 'assets')"
                        class="table-header__button"
                        :class="$q.screen.lt.sm ? 'col-12' : ''"
                        color="accent"
                        size="md"
                    ></q-btn>
                </div>
            </template>
            <template v-slot:body="props">
                <q-tr :props="props" style="display: table-row">
                    <q-td key="name" :props="props">
                        <div>{{ props.row.name }}</div>
                    </q-td>
                    <q-td key="code" :props="props">
                        {{ props.row.code }}
                    </q-td>
                    <q-td key="expected_run_rate" :props="props">
                        <product-expected-run-rates
                            :expectedRunRates="props.row.expected_run_rates"
                            :isEditMode="props.row.isEdit"
                            @changedRunRates="
                                runRates =>
                                    (props.row.expected_run_rates = runRates)
                            "
                        />
                    </q-td>
                    <q-td
                        v-if="props.row.editable_packaging_factors.length > 0"
                        key="packaging_factors"
                    >
                        <div
                            v-for="(factorValue, factor) in props.row
                                .packaging_factors"
                            :key="factor"
                        >
                            <span
                                v-if="
                                    props.row.editable_packaging_factors.includes(
                                        factor
                                    )
                                "
                            >
                                <q-input
                                    v-if="props.row.isEdit"
                                    v-model.number="
                                        props.row.packaging_factors[factor]
                                    "
                                    type="number"
                                    @clear="
                                        props.row.packaging_factors[factor] =
                                            null
                                    "
                                    class="bg-white q-mt-sm"
                                    style="width: 60%"
                                    :rules="[validatePackagingFactor]"
                                    outlined
                                    dense
                                    :label="$t(getSensorLabel(factor)) + ':'"
                                    color="accent"
                                />
                                <span v-else class="q-pa-sm">
                                    {{
                                        `${$t(getSensorLabel(factor))}: ${factorValue}`
                                    }}
                                </span>
                            </span>
                        </div>
                    </q-td>
                    <q-td key="description" :props="props">
                        {{ props.row.description }}
                    </q-td>
                    <q-td key="actions" :props="props">
                        <div
                            style="
                                display: flex;
                                justify-content: flex-start;
                                column-gap: 10px;
                                width: 200px;
                            "
                        >
                            <q-btn
                                v-if="props.row.isEdit"
                                :disable="$userCannot('edit', 'assets')"
                                @click="updateProduct(props.row)"
                                class="table__action-button"
                                color="positive"
                                icon="fas fa-check"
                                outline
                            >
                                <q-tooltip>{{ $t('Save product') }}</q-tooltip>
                            </q-btn>
                            <q-btn
                                v-if="!props.row.isEdit"
                                @click="props.row.isEdit = true"
                                class="table__action-button"
                                color="accent"
                                icon="fas fa-edit"
                                outline
                            >
                                <q-tooltip>{{ $t('Edit product') }}</q-tooltip>
                            </q-btn>
                            <q-btn
                                v-if="props.row.isEdit"
                                @click="
                                    props.row.isEdit = false;
                                    props.row.expected_run_rates =
                                        cloneExpectedRunRates(props.row);
                                "
                                class="table__action-button"
                                icon="fas fa-times"
                                color="negative"
                                outline
                            >
                                <q-tooltip>{{ $t('Cancel edit') }}</q-tooltip>
                            </q-btn>
                            <q-btn
                                id="remove-product-button"
                                @click="remove(props.row)"
                                :disable="$userCannot('edit', 'assets')"
                                :loading="props.row.isLoading"
                                class="table__action-button"
                                color="negative"
                                icon="fas fa-trash-alt"
                                outline
                            >
                                <template v-slot:loading>
                                    <q-spinner />
                                </template>
                                <q-tooltip>{{
                                    $t('Remove product')
                                }}</q-tooltip>
                            </q-btn>
                        </div>
                    </q-td>
                </q-tr>
            </template>

            <!-- No products -->
            <template v-slot:no-data>
                <span class="text-weight-regular">
                    {{ `${$t('No products are linked to this asset')}.` }}
                    <q-btn
                        @click="isSelectMode = true"
                        :label="$t('Click here to add some.')"
                        class="inline-button"
                        color="accent"
                        dense
                        flat
                        no-caps
                    ></q-btn>
                </span>
            </template>

            <!-- Card template -->
            <template v-slot:item="props">
                <product-card
                    @edit="props.row.isEdit = true"
                    @cancel="
                        props.row.isEdit = false;
                        props.row.expected_run_rates = cloneExpectedRunRates(
                            props.row
                        );
                    "
                    @save="
                        fields => {
                            save(fields, props.row);
                        }
                    "
                    @delete="remove(props.row)"
                    :inlineEditMode="props.row.isEdit"
                    :canEdit="$userCan('edit', 'assets')"
                    :canDelete="$userCan('edit', 'assets')"
                    :name="props.row.name"
                    :description="props.row.description"
                    :unit-packing="props.row.unit_packing"
                    :unit-measure="props.row.unit_measure"
                    :code="props.row.code"
                    :expectedRunRates="props.row.expected_run_rates"
                    :editablePackagingFactors="
                        props.row.editable_packaging_factors
                    "
                    :packagingFactors="props.row.packaging_factors"
                >
                </product-card>
            </template>
        </q-table>

        <!-- Available product list -->
        <q-table
            v-else
            @request="onRequest"
            :filter="availableProductsTable.search"
            :rows="products"
            :columns="availableProductsTable.cols"
            v-model:pagination="availableProductsTable.pagination"
            :rows-per-page-options="[
                availableProductsTable.pagination.rowsPerPage
            ]"
            v-model:selected="availableProductsTable.selected"
            class="sticky-table products-table q-ma-none"
            table-style="max-height: none;"
            selection="multiple"
            row-key="id"
            binary-state-sort
            :no-data-label="$t('No data available')"
            flat
        >
            <!-- search bar -->
            <template v-slot:top>
                <div class="col-12 text-h6 q-mb-md">
                    <q-btn
                        @click="isSelectMode = false"
                        :label="$t('Return to asset products')"
                        icon="fas fa-chevron-left"
                        color="primary"
                        flat
                    ></q-btn>
                </div>
                <div class="col-12 text-h6 q-mb-md">
                    {{ $t('Select products for this asset') }}
                </div>
                <div
                    class="fit row wrap justify-between items-start content-start"
                >
                    <q-input
                        data-test="search-product-input"
                        v-model="availableProductsTable.search"
                        @clear="availableProductsTable.search = ''"
                        :placeholder="$t('Search product')"
                        class="col q-mr-md"
                        debounce="750"
                        clearable
                        outlined
                        dense
                        color="accent"
                    >
                        <template v-slot:prepend>
                            <q-icon name="search" />
                        </template>
                    </q-input>
                    <div>
                        <q-btn
                            id="confirm-select-button"
                            @click="confirmSelect"
                            :label="$t('Confirm selection')"
                            class="table-header__button q-mb-md"
                            color="positive"
                            size="md"
                        ></q-btn>
                    </div>
                </div>
            </template>
        </q-table>

        <!-- Product Selection detail dialog  -->
        <q-dialog v-model="detailDialog.show">
            <!-- The card displayed when the accesses is empty -->
            <q-card v-if="noneSelectedProducts">
                <q-card-section>
                    <div class="text-h6">
                        {{ $t('No Selected Products') }}
                    </div>
                </q-card-section>

                <q-card-section class="q-pt-none">
                    <p>
                        {{
                            $t(
                                'You have not selected any product. Please use the table checkbox to pick products.'
                            )
                        }}
                    </p>
                </q-card-section>
                <q-card-actions align="right">
                    <q-btn
                        flat
                        label="OK"
                        @click="$emit('close', [])"
                        color="primary"
                        v-close-popup
                    ></q-btn>
                </q-card-actions>
            </q-card>
            <q-card
                v-else
                :class="$q.screen.gt.md ? 'dialog__lg' : 'dialog__xs'"
            >
                <!-- Dialog header -->
                <q-bar class="bg-primary">
                    <div class="text-white">
                        {{ $t('Selected Products') }}
                    </div>
                    <q-space />

                    <q-btn
                        @click="closeDetailDialog"
                        class="text-white"
                        icon="close"
                        dense
                        flat
                    >
                        <q-tooltip>{{ $t('Close') }}</q-tooltip>
                    </q-btn>
                </q-bar>

                <!-- Selected Products Table -->
                <q-card-section>
                    <div class="col-12 text-grey-9 q-mb-sm">
                        {{
                            $t(
                                'Enter the max run rate and packaging factors for the selected products'
                            )
                        }}
                    </div>
                    <q-table
                        :rows="detailDialog.table.selected"
                        :columns="detailDialog.table.columns"
                        :loading="detailDialog.table.isLoading"
                        :no-data-label="$t('No selected product available')"
                        v-model:pagination="detailDialog.pagination"
                        :hide-bottom="true"
                        table-style="height: 400px;"
                        row-key="id"
                        binary-state-sort
                        flat
                        bordered
                        wrap-cells
                    >
                        <!-- Product name and SKU -->
                        <template v-slot:body-cell-name="props">
                            <q-td
                                key="name"
                                style="min-width: 30%; max-width: 250px"
                                class="ellipsis"
                            >
                                <div>{{ props.row.name }}</div>
                                <div class="text-caption text-grey-7">
                                    {{ props.row.code }}
                                </div>
                            </q-td>
                        </template>

                        <!-- Max run rate column -->
                        <template v-slot:body-cell-expected_run_rate="props">
                            <q-td key="expected_run_rate">
                                <product-expected-run-rates
                                    :expectedRunRates="
                                        props.row.expected_run_rates
                                    "
                                    :isEditMode="true"
                                    :facilityId="facilityId"
                                    @changedRunRates="
                                        runRates =>
                                            (props.row.expected_run_rates =
                                                runRates)
                                    "
                                />
                            </q-td>
                        </template>

                        <!-- Packaging column -->
                        <!-- visible only if the use packaging factor field is true-->
                        <template v-slot:body-cell-packaging_factors="props">
                            <div
                                v-if="
                                    props.row.editable_packaging_factors
                                        .length > 0
                                "
                            >
                                <div
                                    v-for="(factorValue, factor) in props.row
                                        .packaging_factors"
                                    :key="factor"
                                >
                                    <div
                                        v-if="
                                            props.row.editable_packaging_factors.includes(
                                                factor
                                            )
                                        "
                                    >
                                        <q-td :props="props">
                                            <q-input
                                                v-model.number="
                                                    props.row.packaging_factors[
                                                        factor
                                                    ]
                                                "
                                                type="number"
                                                @clear="
                                                    props.row.packaging_factors[
                                                        factor
                                                    ] = null
                                                "
                                                class="q-mt-sm"
                                                :rules="[
                                                    validatePackagingFactor
                                                ]"
                                                outlined
                                                style="width: 200px"
                                                dense
                                                :label="
                                                    $t(
                                                        `${getSensorLabel(
                                                            factor
                                                        )} ` +
                                                            'sensor packaging factor'
                                                    )
                                                "
                                                :hint="
                                                    $t(
                                                        'Multiplication factor to apply on sensor'
                                                    )
                                                "
                                                color="accent"
                                            />
                                        </q-td>
                                    </div>
                                </div>
                            </div>
                        </template>
                    </q-table>
                </q-card-section>

                <!-- Dialog actions -->
                <q-card-section class="q-px-lg float-right">
                    <q-btn
                        :label="$t('Cancel')"
                        class="q-mr-md"
                        color="white"
                        text-color="primary"
                        @click="closeDetailDialog"
                        outline
                    />

                    <q-btn
                        id="confirm-add-product-button"
                        :label="$t('Confirm')"
                        color="primary"
                        @click="submit"
                    />
                </q-card-section>
            </q-card>
        </q-dialog>
    </div>
</template>

<script>
import ProductCard from '@components/ProductCard.vue';
import ProductExpectedRunRates from '@components/ProductExpectedRunRates.vue';
import UpdateURL from '@mixins/UpdateURL.vue';
import { SENSOR_TYPES } from '@models/sensorTypes.js';
import { mapActions, mapState } from 'pinia';
import { useSharedStore } from '@stores/shared.js';
import { useFiltersStore } from '@stores/filters.js';
import { useSensorsStore } from '@stores/sensors.ts';

export default {
    name: 'AssetProductList',
    components: { ProductCard, ProductExpectedRunRates },
    mixins: [UpdateURL],
    emits: ['close'],
    props: {
        asset: {
            type: [Object],
            required: true
        },
        facilityId: {
            type: [Number, String],
            required: true
        }
    },
    watch: {
        isSelectMode: {
            handler() {
                this.initProductList();
            }
        }
    },
    created() {
        // get the store filtered product name
        const storedFilteredProduct = this.getStoredAssetFilter({
            facilityId: this.facilityId,
            assetId: this.asset.id,
            filter: 'products'
        });
        // sync the component's search value with the stored
        // filtered product string.
        this.selectedProductsTable.search = storedFilteredProduct;
        // if there is nothing in the store for filtered product
        // get all the product from the database for this particular
        // asset, otherwise get the product related to the stored
        // filtered.
        if (!this.selectedProductsTable.search) {
            this.initProductList();
        } else {
            this.getProducts();
        }
    },
    data() {
        return {
            products: [],
            isSelectMode: false,
            new_run_rate: false,
            run_rates_to_create: [],
            run_rates_to_delete: [],
            // table configs
            selectedProductsTable: {
                cols: [
                    {
                        align: 'left',
                        field: 'name',
                        name: 'name',
                        label: this.$t('Name'),
                        sortable: true
                    },
                    {
                        align: 'left',
                        field: 'code',
                        name: 'code',
                        label: this.$t('SKU'),
                        sortable: true
                    },
                    {
                        align: 'left',
                        field: 'expected_run_rate',
                        name: 'expected_run_rate',
                        label: this.$t('Max run rate (product/minute)'),
                        sortable: true
                    },
                    {
                        align: 'left',
                        field: 'packaging_factors',
                        name: 'packaging_factors',
                        label: this.$t('Packaging Factors'),
                        classes: this.asset.use_packaging_factors
                            ? ''
                            : 'hidden',
                        headerClasses: this.asset.use_packaging_factors
                            ? ''
                            : 'hidden',
                        sortable: true
                    },
                    {
                        align: 'left',
                        field: 'description',
                        name: 'description',
                        label: this.$t('Description')
                    },
                    {
                        align: 'left',
                        field: 'actions',
                        name: 'actions',
                        label: this.$t('Actions')
                    }
                ],
                pagination: {
                    sortBy: '',
                    descending: false,
                    page: 1,
                    rowsPerPage: 30,
                    rowsNumber: null
                },
                search: ''
            },
            // products that can be linked to this asset
            availableProductsTable: {
                cols: [
                    {
                        align: 'left',
                        field: 'name',
                        name: 'name',
                        label: this.$t('Name'),
                        sortable: true
                    },
                    {
                        align: 'left',
                        field: 'code',
                        name: 'code',
                        label: this.$t('SKU'),
                        sortable: true
                    },
                    {
                        align: 'left',
                        field: 'description',
                        name: 'description',
                        label: this.$t('Description')
                    }
                ],
                pagination: {
                    sortBy: '',
                    descending: false,
                    page: 1,
                    rowsPerPage: 30,
                    rowsNumber: null
                },
                search: '',
                selected: []
            },
            //  Product Selection detail dialog
            detailDialog: {
                show: false,
                pagination: {
                    sortBy: '',
                    descending: false,
                    page: 1,
                    rowsPerPage: 0, // infinite
                    rowsNumber: null
                },
                // event table
                table: {
                    selected: [],
                    columns: [
                        {
                            name: 'name',
                            field: 'name',
                            label: this.$t('Name'),
                            align: 'left'
                        },
                        {
                            name: 'expected_run_rate',
                            field: 'expected_run_rate',
                            label: this.$t('Max run rate (product/minute)'),
                            align: 'left',
                            style: 'width: 35%'
                        },
                        {
                            name: 'packaging_factors',
                            field: 'packaging_factors',
                            label: this.$t('Packaging Factors'),
                            align: 'left',
                            classes: this.asset.use_packaging_factors
                                ? ''
                                : 'hidden',
                            headerClasses: this.asset.use_packaging_factors
                                ? ''
                                : 'hidden'
                        }
                    ],
                    isLoading: false
                }
            },
            failedProducts: [],
            showFailedProductsBanner: false
        };
    },
    computed: {
        ...mapState(useFiltersStore, ['getStoredAssetFilter']),
        ...mapState(useSensorsStore, ['getSensors']),
        /**
         * Base URL to use to fetch products.
         * @return {string}
         */
        baseURL() {
            if (this.isSelectMode) {
                return `assets/${this.asset.id}/available_products/`;
            } else {
                return `assets/${this.asset.id}/products/`;
            }
        },
        /**
         * Get URL with query parameters.
         * @return {String}
         */
        url() {
            // set active table
            let table = null;
            if (this.isSelectMode) {
                table = this.availableProductsTable;
            } else {
                table = this.selectedProductsTable;
            }
            // get current pagination state attributes
            let { page, rowsPerPage, sortBy, descending } = table.pagination;
            if (
                !this.isSelectMode &&
                (sortBy === 'name' || sortBy === 'code')
            ) {
                sortBy = `product__${sortBy}`;
            }
            // update URL
            return this.updateURL({
                baseURL: this.baseURL,
                params: {
                    search: table.search,
                    ordering: descending ? `-${sortBy}` : `${sortBy}`,
                    page: page,
                    // add 'facility' in query if we are in select mode
                    ...(this.isSelectMode && {
                        facility: this.facilityId
                    })
                }
            });
        },
        /**
         * Check if there are selected products.
         * @return {boolean}
         */
        noneSelectedProducts() {
            return this.availableProductsTable.selected.length === 0;
        },
        /**
         * Build payload for selected products in format expected by the server.
         * @return {Object}
         */
        selectedProducts() {
            const selectedProducts = [];
            for (const product of this.detailDialog.table.selected) {
                selectedProducts.push({
                    id: product.id,
                    expected_run_rates: product.expected_run_rates,
                    packaging_factors: product.packaging_factors
                });
            }
            return selectedProducts;
        },
        /**
         * Return sensors linked to asset
         * @return {Object}
         */
        sensors() {
            let sensorsData = this.getSensors(this.asset.id);
            return sensorsData;
        },
        /**
         * Determine if all run rates and packaging factor for the selected products are valid.
         * @return {Boolean}
         */
        isEachRunRateAndPackagingFactorsValid() {
            for (const product of this.selectedProducts) {
                return (
                    this.validateExpectedRunRates(product.expected_run_rates) &&
                    this.validatePackagingFactors(product.packaging_factors)
                );
            }
            return false;
        },
        /**
         * Get the visible columns for the table.
         * Remove packaging factors column if sensor is not linked to asset.
         * @return {Array}
         */
        visibleColumns() {
            const cols = [
                'name',
                'code',
                'actions',
                'description',
                'expected_run_rate'
            ];

            if (this.sensors && this.sensors.length > 0) {
                cols.push('packaging_factors');
            }

            return cols;
        }
    },
    methods: {
        ...mapActions(useSharedStore, ['beginLoading', 'stopLoading']),
        ...mapActions(useFiltersStore, ['setAssetFilter']),
        /***
         * Validate expected run rate object
         */
        validateExpectedRunRates(runRates) {
            if (runRates === null || runRates == []) {
                return false;
            }
            for (const runRate of runRates) {
                if (
                    runRate.start === null ||
                    runRate.start === undefined ||
                    typeof runRate.start !== 'string'
                ) {
                    return false;
                }
                if (runRate.end !== null && typeof runRate.end !== 'string') {
                    return false;
                }
                if (parseFloat(runRate.value) <= 0 || runRate.value === null) {
                    return false;
                }
            }
            return true;
        },
        validatePackagingFactors(packagingFactors) {
            if (
                packagingFactors === null ||
                !Object.values(packagingFactors).every(v => v >= 0)
            ) {
                return false;
            }
            return true;
        },
        /**
         * Get products from the server.
         */
        async getProducts() {
            // Send the API request only if asset.id is an integer
            if (this.asset) {
                try {
                    this.products = [];
                    const response = await this.$axios.get(this.url);
                    response.data.results.forEach(product => {
                        product['isEdit'] = false;
                        // get an array of sensors which can be edited. for example ["reject"]
                        product.editable_packaging_factors =
                            this.getEditablePackagingFactors(
                                this.asset.use_packaging_factors
                            );
                        // copy the expected run rates, this is being done if just in case
                        // user edits the run rates but then decides to cancel the edit, so
                        // we would need to revert the expected run rates to the original ones.
                        product['originalExpectedRunRates'] = structuredClone(
                            product.expected_run_rates
                        );
                        // in case product is not linked, then set the default packaging factors
                        if (!product.packaging_factors) {
                            product.packaging_factors = {
                                production: 1.0,
                                reject: 1.0,
                                net: 1.0
                            };
                        }
                        this.products.push(product);
                    });

                    // update total items count for table
                    if (this.isSelectMode) {
                        this.availableProductsTable.pagination.rowsNumber =
                            response.data.count;
                    } else {
                        this.selectedProductsTable.pagination.rowsNumber =
                            response.data.count;
                    }
                } catch (error) {
                    this.$notifyError(
                        this.$t(
                            'Failed to load products. Please try again later.'
                        ),
                        error
                    );
                }
                this.stopLoading();
            } else {
                this.$notifyError(
                    this.$t('Failed to load products. Please try again later.')
                );
            }
        },
        /**
         * Returns an array of sensors which can be edited.
         * @param {boolean} usePackagingFactors
         * @return {Array}
         */
        getEditablePackagingFactors(usePackagingFactors) {
            let factors = [];
            // if use packaging factors is true, then get editable packagging factors
            if (!usePackagingFactors) {
                return factors;
            }

            for (let sensor in this.sensors) {
                // get type of sensor

                factors.push(this.sensors[sensor]['sensor_type']);
            }

            return factors;
        },
        async onRequest(props) {
            // update pagination object for table
            if (this.isSelectMode) {
                this.availableProductsTable.pagination = props.pagination;
            } else {
                this.selectedProductsTable.pagination = props.pagination;
            }

            // begin loading
            this.beginLoading(`${this.$t('Loading products')}...`);

            // fetch data from the server
            await this.getProducts();
        },
        /**
         * Handle save row.
         * @param {Object}
         */
        async updateProduct(productLink) {
            const expectedRunRates = productLink.expected_run_rates;
            const packagingFactors = productLink.packaging_factors;
            await this.save(
                {
                    expectedRunRates,
                    packagingFactors
                },
                productLink
            );
        },
        /**
         * Save and update of expected run rate, and packaging factors of the selected product.
         * @param {Object} productLink
         */
        async save(productLink, row) {
            if (
                this.validateExpectedRunRates(productLink.expectedRunRates) &&
                this.validatePackagingFactors(productLink.packagingFactors)
            ) {
                const payload = {
                    id: row.product,
                    expected_run_rates: productLink.expectedRunRates.map(
                        runRate => ({
                            ...runRate,
                            value: parseFloat(runRate.value).toFixed(2)
                        })
                    ),
                    packaging_factors: productLink.packagingFactors
                };
                const confirmOrSave = async () => {
                    this.beginLoading(`${this.$t('Saving product')}...`);

                    try {
                        const response = await this.$axios.post(
                            `assets/${this.asset.id}/update_product_rate/`,
                            payload
                        );
                        row.originalExpectedRunRates = structuredClone(
                            productLink.expectedRunRates
                        );
                        row.packaging_factors = productLink.packagingFactors;
                        row.isEdit = false;
                        this.$notifySuccess(
                            this.$t('Products have been updated successfully')
                        );
                    } catch (error) {
                        console.error(error);
                        this.$notifyError(
                            this.$t(
                                'Failed to update products in this asset. Please try again later.'
                            ),
                            error
                        );
                    } finally {
                        this.stopLoading();
                    }
                };
                const sensorsWithZeroPackagingFactor = Object.keys(
                    productLink.packagingFactors
                ).filter(key => productLink.packagingFactors[key] === 0);
                if (sensorsWithZeroPackagingFactor.length > 0) {
                    const message = this.$t(
                        'Setting a packaging factor to 0 will disable the {sensors} sensors for this product. Are you sure you want to proceed?',
                        {
                            sensors: sensorsWithZeroPackagingFactor
                                .map(key => key.toUpperCase())
                                .join(', ')
                        }
                    );

                    // Show confirmation before proceeding with the save
                    this.$confirmZeroPackagingFactor(message).onOk(
                        confirmOrSave
                    );

                    // If one invalid packaging factor is found, stop emitting the event
                    return;
                }
                await confirmOrSave();
            } else {
                this.showProductRunRateErrors();
            }
        },
        /**
         * Unselect a product for this asset.
         */
        async remove(row) {
            this.$confirmDelete(
                this.$t(
                    'Are you sure you want to remove {item} from {asset}?',
                    {
                        item: row.name,
                        asset: this.asset.name
                    }
                )
            ).onOk(async () => {
                // begin loading
                row.isLoading = true;
                // unlink product from asset
                await this.$axios
                    .post(`assets/${this.asset.id}/remove_product/`, {
                        product_ids: [row.product]
                    })
                    .then(response => {
                        // remove item off the table
                        let productIndex = this.products.findIndex(
                            item => item.product === row.product
                        );
                        this.products.splice(productIndex, 1);

                        // decrement total row count
                        this.selectedProductsTable.pagination.rowsNumber--;

                        // notify user
                        this.$notifySuccess(
                            this.$t('Removed product from asset')
                        );
                    })
                    .catch(error => {
                        this.$notifyError(
                            this.$t(
                                'Failed to remove product from this asset. Please try again later.'
                            ),
                            error
                        );
                    })
                    .finally(() => {
                        row.isLoading = false;
                    });
            });
        },
        /**
         * Confirm products selected for this asset.
         */
        confirmSelect() {
            // extract ids from selected products
            const productIds = this.availableProductsTable.selected.map(
                product => product.id
            );

            // reset the selected product list
            this.detailDialog.table.selected = [];

            // assign the selected product to the detail dialog
            this.availableProductsTable.selected.forEach(product => {
                this.detailDialog.table.selected.push({
                    ...product,
                    expected_run_rates: [
                        {
                            start: '2019-01-01T00:00:00Z',
                            end: null,
                            value: null
                        }
                    ] // new field
                });
            });

            // show the detail dialog
            this.detailDialog.show = true;
        },

        /************************************
         * Product Selection Detail Dialog  *
         ***********************************/
        /**
         * Send product links payload to server.
         */
        async submitProductLinks() {
            const payload = {
                products: this.selectedProducts
            };

            try {
                const response = await this.$axios.post(
                    `assets/${this.asset.id}/add_product/`,
                    payload
                );

                if (
                    response.status === 200 &&
                    response.data.success.length > 0
                ) {
                    this.$notifySuccess(this.$t('Updated products in asset'));
                }
                if (response.status === 200 && response.data.error.length > 0) {
                    this.$notifyError(
                        this.$t(
                            'Failed to update products in this asset. Please try again later.'
                        )
                    );

                    this.showFailedProductsBanner = true;
                    this.failedProducts = [];
                    this.detailDialog.table.selected.forEach(product => {
                        if (response.data.error.includes(product.id)) {
                            this.failedProducts.push(product.name);
                        }
                    });
                }
            } catch (error) {
                this.$notifyError(
                    this.$t(
                        'Failed to update products in this asset. Please try again later.'
                    ),
                    error
                );
            }

            // close the detail dialog
            this.closeDetailDialog();
            // return to list view
            this.availableProductsTable.selected = [];
            this.isSelectMode = false;
        },
        /**
         * Trigger validations for each q-input and notify user of errors.
         */
        showProductRunRateErrors() {
            // notify user
            this.$notifyError(
                this.$t(
                    'Please verify max run rates and packaging factors for each linked product'
                )
            );
        },
        /**
         * Handle submission of product links.
         */
        async submit() {
            // validate expected run rate field and packaging factors
            if (this.isEachRunRateAndPackagingFactorsValid) {
                await this.submitProductLinks();
            } else {
                this.showProductRunRateErrors();
            }
        },
        /**
         * Close and reset the detail dialog.
         */
        closeDetailDialog() {
            this.detailDialog.show = false;
            this.detailDialog.table.selected = [];
        },
        /**
         * Validate the run rate input
         * @param {String} value - User input from 'expected_run_rate' field.
         * @return {Boolean} - Valid inputs returns true.
         * @return {String} - Error message.
         */
        validateRunRate(value) {
            // convert the input value to float number
            value = parseFloat(value);

            if (!isNaN(value)) {
                if (value <= 0) {
                    return this.$t('Run rate must be positive');
                }
            } else {
                return this.$t('Enter a positive real number');
            }
            return true;
        },
        /**
         * Validate packaging factor input
         * @param {Number} value - User input from 'packaging factors' field.
         * @return {Boolean} - Valid inputs returns true.
         * @return {String} - Error message.
         */
        validatePackagingFactor(value) {
            if (value < 0) {
                return this.$t('Packaging factor must be positive');
            }
            return true;
        },

        /**
         * Update the filtered product per asset in a particular facility,
         * in the store.
         * Depends on the value of selectedCausesTable.search.
         */
        setFilteredProductInStore() {
            this.setAssetFilter({
                facilityId: this.facilityId,
                assetId: this.asset.id,
                filter: 'products',
                value: this.selectedProductsTable.search
            });
        },
        /**
         * Empty the filtered product per asset in a particular facility,
         * in the store.
         * Empty the value of selectedCausesTable.search.
         */
        removeFilteredProductFromStore() {
            this.setAssetFilter({
                facilityId: this.facilityId,
                assetId: this.asset.id,
                filter: 'products',
                value: this.selectedProductsTable.search
            });
            this.selectedProductsTable.search = '';
        },
        initProductList() {
            // reload list based on view
            let pagination = {};
            if (this.isSelectMode) {
                pagination = this.availableProductsTable.pagination;
            } else {
                pagination = this.selectedProductsTable.pagination;
            }

            // load data
            this.onRequest({
                pagination: pagination,
                filter: ''
            });
        },
        /**
         * clones the expected run rates array of given product
         * @param {object} row product object
         */
        cloneExpectedRunRates(row) {
            return structuredClone(row.originalExpectedRunRates);
        },
        /**
         * Get sensor label.
         * @param {String} sensorType - Sensor type.
         * @return {String} - Sensor label.
         */
        getSensorLabel(sensorType) {
            return SENSOR_TYPES[sensorType]['label'];
        }
    }
};
</script>

<style lang="scss" scoped>
.dialog__lg {
    max-width: 75%;
}

.dialog__xs {
    width: 100%;
    max-width: none;
}

.inline-button :deep(.q-btn__wrapper) {
    padding: 0px;
}

.products-table {
    height: 70vh;
}

// align table top with cards
:deep(.q-table__top) {
    padding: 0px;
}
</style>
